import { motion } from 'framer-motion';
import { Loader2, Sparkles, X } from 'lucide-react';
import { Z_LAYERS } from '../constants/zLayers';
import Button from './Button';
import { AccountForm } from './AccountForm';
import type { UpgradePhase } from '../contexts/UpgradeFlowContext';

/**
 * Dialog for the account-gated premium upgrade flow (ADR-011), rendered by
 * `UpgradeFlowProvider`. Shows the account create/sign-in step for
 * signed-out users, the "opening checkout" busy state, and the checkout
 * failure state with a retry. Sits at NESTED_MODAL because it can open above
 * other modals (PremiumFeatureDialog, FolderEditDialog) that trigger it.
 */

interface UpgradeAccountDialogProps {
  phase: Exclude<UpgradePhase, 'idle'>;
  onClose: () => void;
  onRetry: () => void;
}

export function UpgradeAccountDialog({ phase, onClose, onRetry }: UpgradeAccountDialogProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center"
      style={{ zIndex: Z_LAYERS.NESTED_MODAL }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-transparent-light dark:bg-transparent-dark backdrop-blur-sm rounded-2xl shadow-xl w-[500px] max-w-[90vw] max-h-[90vh] overflow-y-auto flex flex-col border border-gray-200/50 dark:border-gray-700/50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-account-dialog-title"
      >
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <h2
              id="upgrade-account-dialog-title"
              className="text-2xl font-semibold text-gray-800 dark:text-white flex items-center"
            >
              <Sparkles className="w-5 h-5 text-indigo-500 mr-2" />
              {phase === 'account' ? 'Create your account' : 'Premium checkout'}
            </h2>
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
              aria-label="Close upgrade dialog"
            >
              <X className="w-6 h-6" />
            </motion.button>
          </div>
        </div>

        <div className="p-6">
          {phase === 'account' && (
            <>
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                Premium includes cloud sync — your account keeps your purchase and your prompts
                together, on every device. Create an account (or sign in) and we&apos;ll take you
                straight to checkout.
              </p>
              <AccountForm defaultMode="signup" />
            </>
          )}

          {phase === 'openingCheckout' && (
            <div className="flex flex-col items-center py-8 space-y-4">
              <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
              <p className="text-sm text-gray-600 dark:text-gray-300">Opening secure checkout…</p>
            </div>
          )}

          {phase === 'checkoutError' && (
            <div className="space-y-6">
              <p className="text-sm text-gray-600 dark:text-gray-300" role="alert">
                We couldn&apos;t open the checkout page. Check your connection and try again — your
                account is safe and you can also upgrade later from Settings.
              </p>
              <div className="flex justify-end gap-3">
                <Button variant="secondary" onClick={onClose} type="button">
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={onRetry}
                  type="button"
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
